import { IconButton, Stack } from "@mui/material";
import { ReuseEllipsisTooltip } from "../ReuseEllipsisTooltip";
import { ReactNode, useMemo, useState } from "react";
import { Client, UserThatShareCampaign } from "../../../types/apiTypes";
import { TableColumnType } from "../../../types/genereal";
import { PermissionRoleMapper } from "../../company";
import { PersonWithPenIcon } from "../../images";
import { ReuseTooltip } from "../ReuseTooltip";
import { ManagePermissionsModal } from "../modal";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { useUpdateUserPermissionsMutation } from "../../../api";
import { useAppSelector } from "../../../hooks";
import { selectPermissions } from "../../../features/permissionsSlice";

const TextCell = ({ text }: { text: string }) => (
  <ReuseEllipsisTooltip
    tooltip={text}
    children={text}
    style={{
      fontSize: "14px",
      lineHeight: "22px",
      fontWeight: 400,
      color: "#232323",
    }}
  />
);
type ClientRow = UserThatShareCampaign & { index: string; isNoClient: string };
type MapperFn = (row: ClientRow, key: keyof Client) => ReactNode;
type RowClient = "index" | "full_name" | "email" | "role" | "permissions";

type Props = {
  columns: TableColumnType[];
  row: ClientRow;
};

export const UserPermissionListRow = ({ columns, row }: Props) => {
  const { isAllowedPermissionsManagement } = useAppSelector(selectPermissions);
  const [currentUser, setCurrentUser] = useState<UserThatShareCampaign | null>(
    null,
  );

  const [updateUserPermissions, { isLoading }] =
    useUpdateUserPermissionsMutation();

  const mapper: Record<string, MapperFn> = useMemo(
    () => ({
      index: ({ index }: ClientRow) => <TextCell text={index} />,
      full_name: ({ first_name, last_name }: ClientRow) =>
        !last_name && !first_name ? (
          "--"
        ) : (
          <TextCell text={`${first_name} ${last_name}`} />
        ),
      email: ({ email }: ClientRow) => <TextCell text={email} />,
      role: ({ roles }: ClientRow) => {
        if (!roles || !roles[0]) return null;
        return <PermissionRoleMapper role={roles[0]} />;
      },
      permissions: ({ index, isNoClient, ...user }: ClientRow) => (
        <ReuseTooltip
          text={
            isAllowedPermissionsManagement
              ? "Configure which features are visible and editable for this user."
              : ""
          }
          maxWidth="200px"
        >
          <IconButton
            aria-label="more"
            id="long-button"
            aria-haspopup="true"
            onClick={() => setCurrentUser(user)}
            style={{
              padding: 0,
              borderRadius: "8px",
              width: "40px",
              height: "40px",
            }}
            disabled={!isNoClient || !isAllowedPermissionsManagement}
          >
            <PersonWithPenIcon />
          </IconButton>
        </ReuseTooltip>
      ),
    }),
    [row, isAllowedPermissionsManagement],
  );

  const changeUserPermissionsHandler = (user: UserThatShareCampaign) => {
    updateUserPermissions(user)
      .unwrap()
      .then(() => setCurrentUser(null))
      .catch((err) =>
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Failed to Change User Permissions"}
          />,
        ),
      );
  };

  return (
    <Stack
      sx={{
        flexDirection: "row",
        width: "100%",
      }}
    >
      {columns.map(({ key, minWidth, maxWidth, bgColor, textAlign }) => (
        <Stack
          key={key}
          sx={{
            minWidth,
            maxWidth,
            borderBottom: bgColor ? "none" : "1px solid #DEDEDE",
            backgroundColor: bgColor,
            p: "16px 0 16px 16px",
            width: "100%",
            textAlign: textAlign,
            justifyContent: "center",
          }}
        >
          {/*@ts-ignore*/}
          {mapper[key as RowClient](row, key as RowClient)}
        </Stack>
      ))}

      {currentUser && (
        <ManagePermissionsModal
          user={currentUser}
          isOpen={true}
          onClose={() => setCurrentUser(null)}
          onSuccess={changeUserPermissionsHandler}
          isLoading={isLoading}
          role={currentUser.roles[0]}
        />
      )}
    </Stack>
  );
};
